import { InputModalityDetector } from '@angular/cdk/a11y';
import {
  computed,
  DestroyRef,
  Directive,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';

import type { AnchoredOverlayDismissReason, AnchoredPlacement } from '../anchored-overlay';
import { useDisclosureFlyoutOverlay } from './disclosure-flyout-overlay';
import type { DisclosureFlyoutPanelComponent } from './disclosure-flyout-panel.component';
import { SbDisclosureFlyoutGroup, SbDisclosureFlyoutTrigger } from './disclosure-flyout.tokens';

/**
 * Long enough to absorb a pointer that only passes over the trigger, short enough to still feel
 * immediate. Published guidance on hover-revealed navigation puts the floor around here — NN/g
 * wants the pointer to rest before anything appears, Baymard measures 300-500ms across retail — and
 * both Radix and Zag ship 200ms. Below this a rail opens panels the user never aimed at.
 */
export const DISCLOSURE_FLYOUT_OPEN_DELAY = 200;

/**
 * Used instead of the open delay when a sibling is already expanded. A pointer travelling
 * diagonally towards an open panel crosses the items between without dwelling on any of them, so a
 * longer delay keeps the panel the user is heading for. Shorter than this and the crossed item
 * steals the panel; longer and a deliberate move to another item feels stuck.
 */
export const DISCLOSURE_FLYOUT_SWAP_DELAY = 300;

/** Long enough to cross the panel's own gap, which is padding rather than dead space. */
export const DISCLOSURE_FLYOUT_CLOSE_DELAY = 150;

/**
 * Reveals a panel of links beside its trigger, as a disclosure rather than a menu: the trigger
 * keeps whatever role its element already has, the links stay links, and Tab walks them.
 *
 * Deliberately not `role="menu"` — that promises assistive technology a desktop-application menu,
 * with arrow-key navigation and a focus model this does not implement and site navigation does not
 * want.
 *
 * Expanding on focus is keyboard-only. A pointer or touch that lands focus here is already served
 * by the hover path, and a programmatic focus — the one Escape uses to return focus to the trigger
 * — must never re-open what the user just dismissed.
 */
@Directive({
  selector: '[sbDisclosureFlyoutTriggerFor]',
  exportAs: 'sbDisclosureFlyoutTrigger',
  host: {
    '[attr.aria-expanded]': 'expanded()',
    '[attr.aria-controls]': 'panelId()',
    '(focusin)': 'onFocusChange(true)',
    '(focusout)': 'onFocusChange(false)',
    '(mouseenter)': 'onPointerChange(true)',
    '(mouseleave)': 'onPointerChange(false)',
    '(keydown.escape)': 'dismiss()',
  },
  providers: [{ provide: SbDisclosureFlyoutTrigger, useExisting: DisclosureFlyoutTriggerDirective }],
})
export class DisclosureFlyoutTriggerDirective implements SbDisclosureFlyoutTrigger {
  #hostElement = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  #group = inject(SbDisclosureFlyoutGroup, { optional: true });
  #inputModality = inject(InputModalityDetector);

  panel = input.required<DisclosureFlyoutPanelComponent>({ alias: 'sbDisclosureFlyoutTriggerFor' });
  sbDisclosureFlyoutTriggerPlacement = input<AnchoredPlacement>('inline-end-top');
  sbDisclosureFlyoutTriggerDisabled = input(false);
  sbDisclosureFlyoutTriggerExpandOnFocus = input(true);
  sbDisclosureFlyoutTriggerExpandOnHover = input(true);
  sbDisclosureFlyoutTriggerOpenDelay = input(DISCLOSURE_FLYOUT_OPEN_DELAY);
  sbDisclosureFlyoutTriggerSwapDelay = input(DISCLOSURE_FLYOUT_SWAP_DELAY);
  sbDisclosureFlyoutTriggerCloseDelay = input(DISCLOSURE_FLYOUT_CLOSE_DELAY);

  #expanded = signal(false);

  expanded = this.#expanded.asReadonly();
  expandedChange = output<boolean>();

  panelId = computed(() => this.panel().id());

  #focusOnTrigger = signal(false);
  #focusArrivedByKeyboard = signal(false);
  #pointerOnTrigger = signal(false);

  /**
   * Holds the disclosure shut while an intent that would reopen it is still true — after Escape
   * with the pointer still resting on the trigger, and after the group collapsed this one because a
   * sibling opened. Without it the expansion effect simply re-expands on its next run, which for a
   * group turns into two triggers collapsing each other forever.
   *
   * Not a signal: it is read and cleared inside the same effect that would otherwise retrigger on
   * its own write. Apart from a pointer entering the trigger, that effect is the only place it is
   * cleared, so it must never be set while no intent is live — nothing would re-run the effect to
   * clear it, and the next focus would find the disclosure still held shut.
   */
  #suppressed = false;

  /** Focus state seen by the previous expansion run, to tell focus leaving from the pointer leaving. */
  #focusWasInside = false;

  #pendingTimer: ReturnType<typeof setTimeout> | undefined;

  /** Anything that should keep an open panel open. */
  #focusInside = computed(() => this.#focusOnTrigger() || this.panel().focusInside());

  /**
   * The narrower question of what should *reveal* the panel. A pointer that focuses the trigger by
   * clicking it is already served by the hover path, so letting focus open it too would leave the
   * panel pinned open once the pointer has gone.
   */
  #keyboardFocusOnTrigger = computed(() => this.#focusOnTrigger() && this.#focusArrivedByKeyboard());

  #pointerInside = computed(() => this.#pointerOnTrigger() || this.panel().pointerInside());

  constructor() {
    const overlay = useDisclosureFlyoutOverlay({
      anchor: () => this.#hostElement,
      panel: this.panel,
      expanded: this.#expanded,
      placement: this.sbDisclosureFlyoutTriggerPlacement,
      onDismiss: (reason) => this.dismiss(reason),
    });

    // The gap belongs on whichever edge faces the trigger, which a collision flip changes.
    effect(() => this.panel().setPlacement(overlay.placement()));
    effect(() => this.panel().setHosted(overlay.hosted()));

    effect(() => this.#syncExpansion());

    inject(DestroyRef).onDestroy(() => {
      this.#clearPendingTimer();
      this.#group?.releaseExpanded(this);
    });
  }

  onFocusChange(hasFocus: boolean): void {
    if (hasFocus) {
      // Read at arrival: the modality is whatever last drove the document, and a later keypress
      // elsewhere must not retroactively turn a click into a keyboard focus.
      this.#focusArrivedByKeyboard.set(this.#inputModality.mostRecentModality === 'keyboard');
    }

    // No containment check on the way out: with `location: 'inline'` the panel is a DOM *sibling*
    // of the trigger, so this is the only focusout the trigger ever sees. Skipping it for a move
    // into the panel would leave this stuck true for good. `#focusInside` already ORs the panel's
    // own state. The panel's focusin arrives in the same task as this focusout, but not in the same
    // effect flush — which is why `#schedule` never applies a collapse synchronously.
    this.#focusOnTrigger.set(hasFocus);
  }

  onPointerChange(hasPointer: boolean): void {
    // A pointer arriving is a new request to open. Without this, a dismissal that returned focus
    // to the trigger would keep the trigger shut against hover for as long as it holds focus —
    // which, after a link in the panel navigates, is until the user clicks somewhere else.
    if (hasPointer) {
      this.#suppressed = false;
    }

    this.#pointerOnTrigger.set(hasPointer);
  }

  /**
   * Collapses and, for Escape, returns focus to the trigger per the disclosure pattern. An outside
   * click leaves focus where the user put it.
   */
  dismiss(reason: AnchoredOverlayDismissReason = 'escape'): void {
    // Before the expanded check: Escape pressed during a pending open delay has to cancel it, or
    // the panel opens moments after the user asked it not to.
    this.#suppress();

    if (!this.#expanded()) {
      return;
    }

    this.#setExpanded(false);

    if (reason === 'escape') {
      this.#hostElement.focus();
    }
  }

  /** Called by the group when a sibling expands. */
  collapse(): void {
    this.#suppress();
    this.#setExpanded(false);
  }

  #suppress(): void {
    this.#suppressed = untracked(() => this.#focusInside() || this.#pointerInside());
    this.#clearPendingTimer();
  }

  #syncExpansion(): void {
    const focusInside = this.#focusInside();
    const pointerInside = this.#pointerInside();
    const focusJustLeft = this.#focusWasInside && !focusInside;

    this.#focusWasInside = focusInside;

    if (!focusInside && !pointerInside) {
      this.#suppressed = false;
    }

    if (this.sbDisclosureFlyoutTriggerDisabled() || this.#suppressed) {
      this.#schedule(0, false);

      return;
    }

    // Before the `expandOnFocus` branch, and not gated by it: that input governs whether focus on
    // the *trigger* reveals the panel, never whether a panel already holding focus may vanish. With
    // it off, a hover-opened panel would otherwise collapse the moment Tab moved into its links.
    if (this.panel().focusInside()) {
      this.#schedule(0, true);

      return;
    }

    if (this.#keyboardFocusOnTrigger() && this.sbDisclosureFlyoutTriggerExpandOnFocus()) {
      this.#schedule(0, true);

      return;
    }

    if (pointerInside && this.sbDisclosureFlyoutTriggerExpandOnHover()) {
      this.#schedule(this.#openDelay(), true);

      return;
    }

    // The close delay is for a pointer crossing towards the panel. Focus that left went somewhere
    // else on purpose, and the panel must not linger over whatever it went to.
    this.#schedule(focusJustLeft ? 0 : this.sbDisclosureFlyoutTriggerCloseDelay(), false);
  }

  // Untracked throughout: the group's state is both read here and written by #setExpanded, and
  // this runs inside the expansion effect — a tracked read would make that effect retrigger itself.
  #openDelay(): number {
    const expandedSibling = untracked(() => this.#group?.expandedTrigger());

    return expandedSibling && expandedSibling !== this
      ? this.sbDisclosureFlyoutTriggerSwapDelay()
      : this.sbDisclosureFlyoutTriggerOpenDelay();
  }

  #schedule(delay: number, nextExpanded: boolean): void {
    this.#clearPendingTimer();

    // Untracked, like every other read of `#expanded` reachable from the expansion effect: the
    // effect also writes it, and a tracked read would schedule a redundant re-run of itself.
    if (untracked(this.#expanded) === nextExpanded) {
      return;
    }

    // A collapse always waits at least one task, even with no delay. Angular can flush effects at
    // the microtask checkpoint between two native listeners, so this may run after the trigger's
    // focusout or mouseleave but before the panel's focusin or mouseenter. Collapsing there would
    // hide the panel mid-move: the pointer finds nothing to enter, and focus is dropped.
    if (delay === 0 && nextExpanded) {
      this.#setExpanded(nextExpanded);

      return;
    }

    this.#pendingTimer = setTimeout(() => {
      this.#pendingTimer = undefined;
      this.#setExpanded(nextExpanded);
    }, delay);
  }

  #clearPendingTimer(): void {
    if (this.#pendingTimer !== undefined) {
      clearTimeout(this.#pendingTimer);
      this.#pendingTimer = undefined;
    }
  }

  #setExpanded(nextExpanded: boolean): void {
    if (untracked(this.#expanded) === nextExpanded) {
      return;
    }

    this.#expanded.set(nextExpanded);
    this.panel().setExpanded(nextExpanded);

    untracked(() => {
      if (nextExpanded) {
        this.#group?.claimExpanded(this);
      } else {
        this.#group?.releaseExpanded(this);
      }
    });

    this.expandedChange.emit(nextExpanded);
  }
}
